<template>
  <div class="page-wrap">
    <!-- ── Premium Table ─────────────────────── -->
    <PremiumTable
      title="Member Requests"
      subtitle="Approve or reject new member access requests"
      :columns="columns"
      :items="members"
      :loading="loading"
      :total-items="members.length"
      :searchable="true"
      :search="search"
      @search="onSearch"
    >
      <!-- Header extra: block filter -->
      <template #header-extra>
        <div class="header-controls">
          <FilterDropdown
            v-if="useBlocks"
            v-model="selected_flat"
            :options="flats"
            item-text="name"
            item-value="id"
            placeholder="Filter by Block"
            all-label="All"
            @change="getMembers"
          />
          <FilterDropdown
            v-model="selectedType"
            :options="typeOptions"
            item-text="label"
            item-value="value"
            placeholder="Filter by Type"
            all-label="All"
            @change="applyFilters"
          />
        </div>
      </template>

      <!-- Avatar column -->
      <template #col-profile_url="{ item }">
        <div class="mem-avatar-wrap">
          <img
            v-if="item.profile_url"
            :src="API_BASE_URL + '/uploads/' + item.profile_url"
            :alt="item.member_name || item.name"
            class="mem-avatar-img"
            style="cursor: pointer"
            @click.stop="openPhoto(API_BASE_URL + '/uploads/' + item.profile_url, item.member_name)"
          />
          <div
            v-else
            class="mem-avatar-fallback"
            :style="{ background: primaryColor + '20', color: primaryColor }"
          >
            {{ memberInitial(item) }}
          </div>
          <span class="mem-name">{{
            item.member_name || item.name || "—"
          }}</span>
        </div>
      </template>

      <!-- Unit number column -->
      <template #col-house_number="{ item }">
        <div class="house-number-badge">
          <v-icon size="14" color="#94A3B8">mdi-home-outline</v-icon>
          <span v-if="item.flat_name" class="flat-prefix"
            >{{ item.flat_name }}-</span
          >
          <span class="house-num">{{ item.house_number }}</span>
        </div>
      </template>

      <!-- Phone column -->
      <!-- <template #col-mobile_no="{ item }">
        <span class="phone-text"
          >{{ item.country_code }} {{ item.mobile_no }}</span
        >
      </template> -->

      <!-- Request Type column -->
      <template #col-request_type="{ item }">
        <span
          :class="
            item.request_type === 'unit_join'
              ? 'type-tag type-unit'
              : 'type-tag type-member'
          "
        >
          <v-icon size="10">{{
            item.request_type === "unit_join"
              ? "mdi-home-plus-outline"
              : "mdi-account-plus-outline"
          }}</v-icon>
          {{ item.request_type === "unit_join" ? "Unit Join" : "New Member" }}
        </span>
      </template>

      <!-- Status column -->
      <template #col-status="{ item }">
        <span :class="statusClass(item.status)">
          {{ formatStatus(item.status) }}
        </span>
      </template>

      <!-- Mobile Number -->
      <template #col-mobile_no="{ item }">
        <div
          style="
            display: flex;
            align-items: center;
            font-size: 13px;
            font-weight: 600;
            color: var(--tx2);
          "
        >
          <v-icon size="14" color="#94A3B8" class="mr-1"
            >mdi-phone-outline</v-icon
          >
          <span class="phone-text"
            >{{
              item.country_code
                ? (item.country_code.toString().startsWith("+")
                    ? item.country_code
                    : "+" + item.country_code) + " "
                : ""
            }}{{ item.mobile_no || "—" }}</span
          >
        </div>
      </template>

      <!-- Row actions -->
      <template #actions="{ item }">
        <!-- Member registration: always pending, show view + accept + reject -->
        <div v-if="item.request_type === 'member'">
          <ActionButtons
            :show-accept="true"
            :show-reject="true"
            accept-label="Accept Request"
            reject-label="Reject Request"
            @accept="openAccept(item)"
            @reject="openReject(item)"
          />
        </div>

        <!-- Unit join request: show approve/reject only if pending -->
        <div v-else-if="item.request_type === 'unit_join'">
          <div v-if="item.status === 'pending'">
            <ActionButtons
              :show-accept="true"
              :show-reject="true"
              accept-label="Approve Unit Request"
              reject-label="Reject Unit Request"
              @accept="openUnitApprove(item)"
              @reject="openUnitReject(item)"
            />
          </div>
          <span v-else class="reviewed-badge">
            <v-icon
              size="13"
              :color="item.status === 'approved' ? '#16a34a' : '#dc2626'"
            >
              {{
                item.status === "approved"
                  ? "mdi-check-circle"
                  : "mdi-close-circle"
              }}
            </v-icon>
            {{ item.status === "approved" ? "Approved" : "Rejected" }}
          </span>
        </div>
      </template>
    </PremiumTable>

    <!-- ── Confirm Accept (Member Registration) ──── -->
    <ConfirmModal
      v-model="acceptDialog"
      type="primary"
      title="Accept Member Request"
      message="Are you sure you want to approve this member request? They will gain access to the society portal."
      confirm-text="Yes, Approve"
      cancel-text="Cancel"
      :loading="actionLoading"
      @confirm="acceptMemberRequest"
    />

    <!-- ── Confirm Reject (Member Registration) ──── -->
    <ConfirmModal
      v-model="rejectDialog"
      type="danger"
      title="Reject Member Request"
      message="Are you sure you want to reject this request?"
      confirm-text="Yes, Reject"
      cancel-text="Cancel"
      :loading="actionLoading"
      @confirm="rejectMemberRequest"
    />

    <!-- ── Confirm Approve (Unit Join) ───────────── -->
    <ConfirmModal
      v-model="unitApproveDialog"
      type="primary"
      title="Approve Unit Join Request"
      :message="`Approve ${
        activeItem ? activeItem.member_name || activeItem.name : ''
      }'s request to link unit ${
        activeItem
          ? (activeItem.flat_name ? activeItem.flat_name + '-' : '') +
            (activeItem.house_number || '')
          : ''
      }? They will be added to this unit.`"
      confirm-text="Yes, Approve"
      cancel-text="Cancel"
      :loading="actionLoading"
      @confirm="approveUnitRequest"
    />

    <!-- ── Reject Unit Join (with reason) ────────── -->
    <v-dialog v-model="unitRejectDialog" max-width="480" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="text-h6 font-weight-bold pb-2">
          Reject Unit Join Request
          <v-spacer />
          <v-btn icon @click="unitRejectDialog = false"
            ><v-icon>mdi-close</v-icon></v-btn
          >
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <p class="text-body-2 mb-4" style="color: #6b7280">
            Are you sure you want to reject
            <strong>{{
              activeItem ? activeItem.member_name || activeItem.name : ""
            }}</strong
            >'s unit join request?
          </p>
          <v-textarea
            v-model="rejectionReason"
            outlined
            dense
            rounded
            label="Rejection Reason (optional)"
            placeholder="Provide a reason to the member..."
            rows="3"
            class="rounded-lg"
          />
        </v-card-text>
        <v-card-actions class="px-6 pb-5 justify-end" style="gap: 10px">
          <v-btn text class="px-6" @click="unitRejectDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            depressed
            class="px-8"
            color="error"
            :loading="actionLoading"
            @click="rejectUnitRequest"
          >
            Yes, Reject
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <MediaViewer
      v-model="mediaViewer.open"
      :items="mediaViewer.items"
    />
  </div>
</template>

<script>
import { mapMutations, mapGetters, mapState } from "vuex";
import moment from "moment";
import PremiumTable from "../../common/PremiumTable.vue";
import ConfirmModal from "../../common/ConfirmModal.vue";
import ActionButtons from "../../common/ActionButtons.vue";
import FilterDropdown from "../../common/FilterDropdown.vue";
import MediaViewer from "../../common/MediaViewer.vue";
import filterMixin from "../../../mixins/filterMixin";

export default {
  name: "MemberRequest",
  mixins: [filterMixin],
  components: { PremiumTable, ConfirmModal, ActionButtons, FilterDropdown, MediaViewer },

  directives: {},

  data() {
    return {
      allMembers: [],
      members: [],
      loading: false,
      actionLoading: false,
      acceptDialog: false,
      rejectDialog: false,

      // Unit join dialogs
      unitApproveDialog: false,
      unitRejectDialog: false,
      rejectionReason: "",

      activeItem: null,
      flats: [],
      selected_flat: "",
      selectedType: "",
      searchQuery: "",

      typeOptions: [
        { label: "New Member", value: "member" },
        { label: "Unit Join", value: "unit_join" },
      ],
      user: {},
      search: "",
      mediaViewer: { open: false, items: [] },
    };
  },

  computed: {
    ...mapState("theme", ["primaryColor"]),
    ...mapGetters("society", ["useBlocks", "unitType"]),
    API_BASE_URL: () => process.env.VUE_APP_API_URL,

    columns() {
      return [
        { key: "profile_url", label: "Member", width: "220px" },
        {
          key: "house_number",
          label: this.$t("membersrequest.headers.houseNo") || "Unit",
        },
        {
          key: "mobile_no",
          label: this.$t("membersrequest.headers.ContactNo") || "Contact",
        },
        { key: "request_type", label: "Type" },
        { key: "status", label: "Status" },
      ];
    },
  },

  watch: {
    search() {
      this.getMembers();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),

    memberInitial(item) {
      const name = item.member_name || item.name || "";
      return name ? name.charAt(0).toUpperCase() : "U";
    },

    onSearch(val) {
      this.searchQuery = val || "";
      this.applyFilters();
    },

    applyFilters() {
      let list = [...this.allMembers];

      if (this.selectedType) {
        list = list.filter((m) => m.request_type === this.selectedType);
      }

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(
          (m) =>
            (m.member_name || m.name || "").toLowerCase().includes(q) ||
            (m.house_number &&
              String(m.house_number).toLowerCase().includes(q)) ||
            (m.mobile_no && String(m.mobile_no).includes(q)),
        );
      }

      this.members = list;
      this.search = this.searchQuery;
    },

    getMembers() {
      this.loading = true;
      let flat_id = this.selected_flat;
      let unit_type = "";

      if (flat_id === "all_shops") {
        flat_id = "";
        unit_type = 2;
      }

      this.$axios
        .get("societyadmin/requestList", {
          params: {
            flat_id: flat_id || "",
            unit_type: unit_type,
            search: this.search || "",
          },
        })
        .then((response) => {
          this.loading = false;
          const data = response.data;
          if (data.status) {
            this.allMembers = data.members;
            this.applyFilters();
            if (this.search) this.onSearch(this.search);
          } else {
            this.showError(data.message);
            if (data.type === "invalid") {
              localStorage.removeItem("user");
              this.$router.push("/societyadmin/login");
            }
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },

    // ── Member registration actions ───────────────────────────────
    openAccept(item) {
      this.activeItem = item;
      this.acceptDialog = true;
    },
    openReject(item) {
      this.activeItem = item;
      this.rejectDialog = true;
    },

    acceptMemberRequest() {
      if (!this.activeItem) return;
      this.actionLoading = true;
      this.$axios
        .post("societyadmin/members/request/accept/" + this.activeItem.id)
        .then((response) => {
          this.actionLoading = false;
          const data = response.data;
          if (data.status) {
            this.acceptDialog = false;
            this.removeItem(this.activeItem);
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "3000",
              message: data.message,
            });
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.actionLoading = false;
        });
    },

    rejectMemberRequest() {
      if (!this.activeItem) return;
      this.actionLoading = true;
      this.$axios
        .post("societyadmin/members/request/reject/" + this.activeItem.id)
        .then((response) => {
          this.actionLoading = false;
          const data = response.data;
          if (data.status) {
            this.rejectDialog = false;
            this.removeItem(this.activeItem);
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "3000",
              message: data.message,
            });
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.actionLoading = false;
        });
    },

    // ── Unit join request actions ─────────────────────────────────
    openUnitApprove(item) {
      this.activeItem = item;
      this.unitApproveDialog = true;
    },
    openUnitReject(item) {
      this.activeItem = item;
      this.rejectionReason = "";
      this.unitRejectDialog = true;
    },

    approveUnitRequest() {
      if (!this.activeItem) return;
      this.actionLoading = true;
      this.$axios
        .post(`societyadmin/unit-requests/${this.activeItem.id}/review`, {
          status: "approved",
        })
        .then((response) => {
          this.actionLoading = false;
          const data = response.data;
          if (data.status) {
            this.unitApproveDialog = false;
            this.updateItemStatus(this.activeItem.id, "approved");
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "3000",
              message: "Unit join request approved!",
            });
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.actionLoading = false;
        });
    },

    rejectUnitRequest() {
      if (!this.activeItem) return;
      this.actionLoading = true;
      this.$axios
        .post(`societyadmin/unit-requests/${this.activeItem.id}/review`, {
          status: "rejected",
          rejection_reason: this.rejectionReason,
        })
        .then((response) => {
          this.actionLoading = false;
          const data = response.data;
          if (data.status) {
            this.unitRejectDialog = false;
            this.updateItemStatus(this.activeItem.id, "rejected");
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: "3000",
              message: "Request rejected.",
            });
          } else {
            this.showError(data.message);
          }
        })
        .catch(() => {
          this.actionLoading = false;
        });
    },

    // ── Helpers ───────────────────────────────────────────────────
    removeItem(item) {
      const idx = this.allMembers.findIndex(
        (m) => m.id === item.id && m.request_type === item.request_type,
      );
      if (idx !== -1) this.allMembers.splice(idx, 1);
      this.applyFilters();
    },

    updateItemStatus(id, status) {
      const idx = this.allMembers.findIndex(
        (m) => m.id === id && m.request_type === "unit_join",
      );
      if (idx !== -1)
        this.$set(this.allMembers, idx, { ...this.allMembers[idx], status });
      this.applyFilters();
    },

    getFlats() {
      this.$axios.get("societyadmin/flats").then((response) => {
        const data = response.data;
        if (data.status) {
          let flats = [...data.flats];
          if (this.unitType === 3 && this.useBlocks) {
            flats.unshift({ id: "all_shops", name: "All Shops" });
          }
          this.flats = flats;
        }
      });
    },

    formatStatus(status) {
      const map = {
        pending: "Pending",
        approved: "Approved",
        rejected: "Rejected",
      };
      return map[status] || "—";
    },

    statusClass(status) {
      const map = {
        pending: "ptag ptag-amber",
        approved: "ptag ptag-green",
        rejected: "ptag ptag-red",
      };
      return map[status] || "ptag";
    },

    statusIcon(status) {
      return (
        {
          pending: "mdi-clock-outline",
          approved: "mdi-check-circle-outline",
          rejected: "mdi-close-circle-outline",
        }[status] || "mdi-help-circle-outline"
      );
    },

    statusIconColor(status) {
      return (
        { pending: "#92600A", approved: "#15803d", rejected: "#dc2626" }[
          status
        ] || "#6b7280"
      );
    },

    getFormattedDate(date) {
      return date ? moment(date).format("DD MMM YYYY, hh:mm A") : "—";
    },

    showError(msg) {
      this.setAlert({
        showAlert: true,
        variant: "red accent-3",
        timeout: "3000",
        message: msg,
      });
    },

    openPhoto(url, name) {
      this.mediaViewer.items = [{ url, type: "image", name: name || "Photo" }];
      this.mediaViewer.open = true;
    },
  },

  mounted() {
    this.user = localStorage.getItem("user")
      ? JSON.parse(localStorage.getItem("user"))
      : {};

    // Initialize persistent filters
    const saved = this.initFilters("member_request");
    if (saved) {
      if (saved.search) this.search = saved.search;
    }

    this.getMembers();
    this.getFlats();
  },
};
</script>

<style scoped>
.page-wrap {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* ── Avatar ── */
.mem-avatar-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}
.mem-avatar-img {
  width: 34px;
  height: 34px;
  min-width: 34px;
  min-height: 34px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  border: 2px solid var(--border, #e2e8f0);
}
.mem-avatar-fallback {
  width: 34px;
  height: 34px;
  min-width: 34px;
  min-height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}
.mem-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--tx, #111827);
}
.house-number-badge {
  display: inline-flex;
  align-items: center;
  padding: 6px;
  border-radius: 8px;
  background: var(--surface2, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  font-size: 12px;
  /* font-weight: 700; */
  color: var(--tx, #111827);
}
.house-number-badge .flat-prefix {
  color: var(--tx3, #6b7280);
}
.house-number-badge .house-num {
  color: var(--tx3, #6b7280);
}
.phone-text,
.date-text {
  font-size: 12.5px;
  color: var(--tx2, #374151);
}
.ptag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px 3px 7px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
}
.ptag::before {
  content: "";
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.ptag-amber {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
}
.ptag-amber::before {
  background: #f59e0b;
}

.reviewed-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--tx2, #6b7280);
}
</style>
